import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { presentedNativeRoot } from "feat/nativeNavigation/root-state";

/** A guest link being resolved. While one is pending, the pre-auth root stays up. */
let pending: string | null = null;

// Whether `pending` has already been pushed, so a repeat flush does not push it again.
let applied = false;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** The pending guest link's path, or null when none is pending. */
export function pendingGuestLink(): string | null {
  return pending;
}

/** Subscribes to changes in the pending guest link. Returns an unsubscribe function. */
export function subscribePendingGuestLink(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Whether a push is needed to land the pending link's path. Always off iOS, and on iOS only
 * when the auth root is already presented, since a root swap carries the path there itself.
 */
function needsPush(): boolean {
  return !isIosNativeNavigation() || presentedNativeRoot() === "auth";
}

/** Pushes the pending link's path once the router can take it, at most once per link. */
function applyPendingGuestLink(): void {
  if (!pending || applied || typeof window.pushPath !== "function") return;
  applied = true;
  if (!needsPush()) return;
  void window.pushPath(pending);
}

/** Starts resolving a guest link at `path`. Replaces any pending link. */
export function setPendingGuestLink(path: string): void {
  pending = path;
  applied = false;
  notify();
  applyPendingGuestLink();
}

/** Applies the pending guest link now the router is ready. Called by GlobalRouter once it is. */
export function flushPendingGuestLink(): void {
  applyPendingGuestLink();
}

/** Clears the pending guest link. The only way back down to the tabs root. */
export function clearPendingGuestLink(): void {
  if (!pending) return;
  pending = null;
  applied = false;
  notify();
}
